import { useState, useEffect } from 'react'
import { useQuery, useApolloClient } from '@apollo/client'
import {
  GET_CATEGORIES,
  GET_PRODUCTS,
  GET_PRODUCT,
  GET_VARIANT_OPTIONS_FOR_WIZARD,
  GET_BUNDLES_FOR_VARIANT_GROUPED
} from '../graphql/product-queries'
import { Category, Product } from '../types/product'
import CategorySidebar from '../components/CategorySidebar'
import ProductGrid from '../components/ProductGrid'
import { ChevronLeftIcon, ChevronRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ProductWizard from '../components/wizard/ProductWizard'
import BundleWizard from '../components/wizard/BundleWizard'
import { useCart } from '../contexts/CartContext'
import CartDisplay from '../components/CartDisplay'
import SoldOutButton from '../components/SoldOutButton'
import toast from 'react-hot-toast'
import { usePermissions } from '../hooks/usePermissions'
import { useProductAvailability } from '../hooks/useProductAvailability'
import { PERMISSIONS } from '../lib/permissions'

const CashRegistryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [categorySidebarCollapsed, setCategorySidebarCollapsed] = useState(false)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [isBundleWizardOpen, setIsBundleWizardOpen] = useState(false)
  const [isDeciding, setIsDeciding] = useState(false)
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { addItem, cart, replaceItem } = useCart()
  const apollo = useApolloClient()
  const { hasPermission } = usePermissions()

  // Keeps every product's sold-out state fresh (30 s poll). The tiles below and the wizards'
  // meal-deal parts read the same normalized Product entries, so a mark made at the kitchen
  // display or on another terminal reaches this register without a reload — and the wizard
  // queries can stay cache-first.
  useProductAvailability()

  // Fetch categories
  const { data: categoriesData, loading: categoriesLoading } = useQuery(GET_CATEGORIES, {
    variables: { filters: { display_in_pos: true } }
  })

  // Fetch products for selected category
  const { data: productsData, loading: productsLoading } = useQuery(GET_PRODUCTS, {
    variables: { 
      filters: { 
        category_id: selectedCategory ? Number(selectedCategory.id) : undefined,
        display_in_pos: true
      } 
    },
    skip: !selectedCategory
  })

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category)
  }

  const handleProductSelect = async (product: Product) => {
    if (product.sold_out) {
      const canMark = hasPermission(PERMISSIONS.PRODUCT_AVAILABILITY)
      toast(
        (t) => (
          <span>
            {product.name} is sold out{canMark && ' — change it on the Availability page'}
            {canMark && (
              <button
                onClick={() => {
                  toast.dismiss(t.id)
                  navigate('/availability')
                }}
                className="ml-3 underline font-semibold"
              >
                Open
              </button>
            )}
          </span>
        ),
        { id: `sold-out-${product.id}` }
      )
      return
    }
    if (isDeciding) return
    setIsDeciding(true)
    try {
      // Always keep selected product in state for wizard path
      setSelectedProduct(product)

      // If this is a bundle product, open the BundleWizard
      if (String((product as any).kind || '').toLowerCase() === 'bundle') {
        setIsBundleWizardOpen(true)
        return
      }

      const variantCount = (product.variants || []).length
      const firstVariant = product.variants?.[0]
      const isDefaultVariant = variantCount === 1 && firstVariant?.name === 'Default'

      // If multiple variants, OR a single variant that is NOT "Default", we need the wizard to choose/confirm
      if (variantCount > 1 || (variantCount === 1 && !isDefaultVariant)) {
        setIsWizardOpen(true)
        return
      }

      // Single-variant path for "Default" variant: determine the id directly
      const selectedVariantId = Number(firstVariant?.id)
      if (!selectedVariantId) {
        // Fallback: open wizard if we cannot determine variant id
        setIsWizardOpen(true)
        return
      }

      // Fetch option groups (with items) and grouped bundles in parallel (prefer cache if warmed)
      const [optionsResp, bundlesResp] = await Promise.all([
        apollo.query({
          query: GET_VARIANT_OPTIONS_FOR_WIZARD,
          variables: { variantId: Number(selectedVariantId) },
          fetchPolicy: 'cache-first'
        }),
        apollo.query({
          query: GET_BUNDLES_FOR_VARIANT_GROUPED,
          variables: { variantId: Number(selectedVariantId) },
          fetchPolicy: 'cache-first'
        })
      ])

      const optionGroups = (optionsResp?.data?.variantOptionGroupsByVariant || [])
      const hasCustomize = optionGroups.some((group: any) => {
        const minSelect = Number(group?.option_group?.min_select ?? 0)
        const items = group?.option_group?.option_items || []
        return minSelect > 0 || items.length > 0
      })

      const bundles = (bundlesResp?.data?.bundlesForEligibleVariantGrouped || [])
      const inMainMeal = bundles.some((bp: any) =>
        (bp.groups || []).some((g: any) => {
          const isMain = typeof g.name === 'string' && g.name.trim().toLowerCase() === 'main'
          if (!isMain) return false
          const hasThisVariant = (g.products || []).some((p: any) => (p.options || []).some((opt: any) => Number(opt.eligible_variant?.id) === Number(selectedVariantId)))
          return hasThisVariant
        })
      )

      if (!hasCustomize && !inMainMeal) {
        // Direct add to cart; no modal
        const selectedVariant = (product.variants || []).find(v => Number(v.id) === Number(selectedVariantId))
        if (selectedVariant) {
          addItem({
            type: 'main',
            product_id: Number(product.id),
            product_variant_id: Number(selectedVariant.id),
            product_variant_name: selectedVariant.name,
            product_name: product.name,
            price_cents: selectedVariant.price_cents,
            deposit_cents: Number((selectedVariant as any).deposit_cents || 0),
            quantity: 1,
            selected_options: []
          })
        }
        // Keep wizard closed
        setSelectedProduct(null)
        setIsWizardOpen(false)
        return
      }

      // Otherwise open the wizard for the remaining steps
      setIsWizardOpen(true)
    } finally {
      setIsDeciding(false)
    }
  }

  const handleWizardClose = () => {
    setIsWizardOpen(false)
    setSelectedProduct(null)
    setEditingItemId(null)
  }

  const handleEditItem = async (itemId: string) => {
    const item = cart.items.find(it => it.id === itemId)
    if (!item || !item.product_id) return
    try {
      const { data } = await apollo.query({
        query: GET_PRODUCT,
        variables: { id: item.product_id },
        fetchPolicy: 'cache-first',
      })
      if (!data?.product) return
      setEditingItemId(itemId)
      setSelectedProduct(data.product)
      if (String(data.product.kind || '').toLowerCase() === 'bundle') {
        setIsBundleWizardOpen(true)
      } else {
        setIsWizardOpen(true)
      }
    } catch (err) {
      console.error('Failed to fetch product for edit:', err)
    }
  }

  // Add-to-cart or replace-in-cart from wizard state
  const handleAddToCart = (wizardState: any) => {
    const isBundleProduct = selectedProduct && String((selectedProduct as any).kind || '').toLowerCase() === 'bundle'
    if (!selectedProduct) return

    const isEditing = !!editingItemId
    // Collect all new items so we can either add or replace
    const newItems: any[] = []

    if (isBundleProduct) {
      const mainVariantId = Number(wizardState.selectedVariantId || 0)
      const mainId = `${mainVariantId}-${Date.now()}-${Math.random()}`
      const hasMealParts = Object.keys(wizardState.selectedMealParts || {}).length > 0
      newItems.push({
        type: 'main',
        id: mainId,
        product_id: Number(selectedProduct.id),
        is_meal_deal: hasMealParts,
        product_variant_id: mainVariantId,
        product_variant_name: wizardState.selectedMainVariantName || '',
        product_name: wizardState.selectedMainProductName || selectedProduct.name,
        price_cents: Number(wizardState.selectedMainVariantPriceCents || 0),
        deposit_cents: Number(wizardState.selectedMainVariantDepositCents || 0),
        quantity: 1,
        selected_options: wizardState.selectedOptionDetails || []
      })
      Object.entries(wizardState.selectedMealParts || {}).forEach(([groupId, variantId]) => {
        const gid = Number(groupId)
        const labelInfo = wizardState.mealVariantNames?.[gid]
        const groupName = wizardState.bundleGroupNames?.[gid]
        newItems.push({
          type: 'meal_part',
          id: `meal-${gid}-${Date.now()}-${Math.random()}`,
          product_variant_id: Number(variantId),
          product_variant_name: labelInfo?.variantName || '',
          product_name: labelInfo ? labelInfo.productName : `Meal Part ${variantId}`,
          // The option's surcharge (+€0,50 for a premium drink; 0 for the
          // included can). The API prices INCLUDED_PART lines from the
          // catalog, so the cart must match or the ±2 cent check rejects it.
          price_cents: Number(wizardState.mealVariantPriceDeltas?.[gid] || 0),
          // Free inside the deal, but a can is still a can: the API charges
          // statiegeld on meal-deal parts from the variant, so the cart must
          // include it or the totals disagree and the order is rejected.
          deposit_cents: Number(wizardState.mealVariantDeposits?.[gid] || 0),
          quantity: 1,
          selected_options: [],
          bundle_parent_id: mainId,
          bundle_group_id: gid,
          bundle_group_name: groupName
        })
      })
    } else {
      const selectedVariant = selectedProduct.variants.find(v => Number(v.id) === Number(wizardState.selectedVariantId))
      if (!selectedVariant) return

      const isMealDeal = !!wizardState.selectedBundle
      const mainId = `${selectedVariant.id}-${Date.now()}-${Math.random()}`
      newItems.push({
        type: 'main',
        id: mainId,
        product_id: Number(selectedProduct.id),
        is_meal_deal: isMealDeal,
        product_variant_id: selectedVariant.id,
        product_variant_name: selectedVariant.name,
        product_name: selectedProduct.name,
        price_cents: selectedVariant.price_cents,
        deposit_cents: Number((selectedVariant as any).deposit_cents || 0),
        quantity: 1,
        selected_options: wizardState.selectedOptionDetails || []
      })

      if (wizardState.selectedBundle) {
        const bundleVariantId = Number(wizardState.selectedBundleVariantId || 0)
        newItems.push({
          type: 'bundle',
          id: `bundle-${Date.now()}-${Math.random()}`,
          product_variant_id: bundleVariantId > 0 ? bundleVariantId : Number(selectedVariant.id),
          product_variant_name: '',
          product_name: 'Upgrade to meal deal',
          price_cents: wizardState.selectedBundle.price_delta_cents,
          quantity: 1,
          selected_options: [],
          bundle_parent_id: mainId,
          bundle_group_id: wizardState.selectedBundle.bundle_group_id
        })
      }

      Object.entries(wizardState.selectedMealParts || {}).forEach(([groupId, variantId]) => {
        const gid = Number(groupId)
        const labelInfo = wizardState.mealVariantNames?.[gid]
        const groupName = wizardState.bundleGroupNames?.[gid]
        if (typeof groupName === 'string' && groupName.trim().toLowerCase() === 'main') return
        newItems.push({
          type: 'meal_part',
          id: `meal-${gid}-${Date.now()}-${Math.random()}`,
          product_variant_id: Number(variantId),
          product_variant_name: labelInfo?.variantName || '',
          product_name: labelInfo ? labelInfo.productName : `Meal Part ${variantId}`,
          // The option's surcharge (+€0,50 for a premium drink; 0 for the
          // included can). The API prices INCLUDED_PART lines from the
          // catalog, so the cart must match or the ±2 cent check rejects it.
          price_cents: Number(wizardState.mealVariantPriceDeltas?.[gid] || 0),
          // Free inside the deal, but a can is still a can: the API charges
          // statiegeld on meal-deal parts from the variant, so the cart must
          // include it or the totals disagree and the order is rejected.
          deposit_cents: Number(wizardState.mealVariantDeposits?.[gid] || 0),
          quantity: 1,
          selected_options: [],
          bundle_parent_id: mainId,
          bundle_group_id: gid,
          bundle_group_name: groupName
        })
      })
    }

    if (isEditing) {
      replaceItem(editingItemId!, newItems)
    } else {
      newItems.forEach(item => addItem(item))
    }

    // BundleWizard calls onClose itself; ProductWizard needs explicit close
    if (!isBundleProduct) {
      handleWizardClose()
    }
  }

  const categories = categoriesData?.categories || []
  const products = productsData?.products || []

  // Prefetch wizard data for all visible products to warm the Apollo cache
  useEffect(() => {
    if (!products || products.length === 0) return
    products.slice(0, 12).forEach((p: any) => {
      const variants = p?.variants || []
      variants.forEach((v: any) => {
        const variantId = Number(v?.id)
        if (!variantId) return
        apollo.query({
          query: GET_VARIANT_OPTIONS_FOR_WIZARD,
          variables: { variantId },
          fetchPolicy: 'cache-first'
        }).catch(() => {})
        apollo.query({
          query: GET_BUNDLES_FOR_VARIANT_GROUPED,
          variables: { variantId },
          fetchPolicy: 'cache-first'
        }).catch(() => {})
      })
    })
  }, [products, apollo])

  // Handle escape key to exit fullscreen
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isFullScreen) {
      document.exitFullscreen()
      setIsFullScreen(false)
    }
  }

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isFullScreen])

  // Exit fullscreen when component unmounts
  useEffect(() => {
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      }
    }
  }, [])

  // Listen for fullscreen changes to keep state in sync
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  // Auto-enter fullscreen when component mounts (if not already in fullscreen)
  useEffect(() => {
    const autoFullscreen = searchParams.get('fullscreen') !== 'false'
    if (autoFullscreen && !document.fullscreenElement) {
      const timer = setTimeout(() => {
        document.documentElement.requestFullscreen().catch(() => {
        })
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [searchParams])

  return (
    <div className={`${isFullScreen ? 'fixed inset-0 z-50' : 'h-screen'} flex flex-col bg-gray-50`}>
      {/* Persistent Navigation Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              if (isFullScreen) {
                document.exitFullscreen()
                setIsFullScreen(false)
              }
              navigate('/dashboard')
            }}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Back to Dashboard</span>
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <SoldOutButton />
          <span className="text-sm text-gray-500">POS Cash Registry</span>
          {isFullScreen && (
            <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Fullscreen Mode</span>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex min-h-0">
        {/* Category Sidebar */}
        <div className={`bg-white border-r border-gray-200 flex-shrink-0 transition-all duration-300 ${
          categorySidebarCollapsed ? 'w-16' : 'w-80'
        }`}>
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              {!categorySidebarCollapsed && (
                <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
              )}
              <button
                onClick={() => setCategorySidebarCollapsed(!categorySidebarCollapsed)}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                title={categorySidebarCollapsed ? 'Expand categories' : 'Collapse categories'}
              >
                {categorySidebarCollapsed ? (
                  <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                )}
              </button>
            </div>
            <div className="flex-1">
              <CategorySidebar
                categories={categories}
                loading={categoriesLoading}
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
                collapsed={categorySidebarCollapsed}
              />
            </div>
          </div>
        </div>

        {/* Products Area */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {selectedCategory ? selectedCategory.name : 'Select a Category'}
              </h1>
              {selectedCategory && (
                <p className="text-sm text-gray-600 mt-1">{products.length} products available</p>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {selectedCategory ? (
              <ProductGrid
                products={products}
                loading={productsLoading}
                onProductSelect={handleProductSelect}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 text-gray-400">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No category selected</h3>
                  <p className="mt-1 text-sm text-gray-500">Select a category from the sidebar to view products</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cart Area */}
        <div className="w-[28rem] bg-white border-l border-gray-200 flex flex-col min-h-0">
          <CartDisplay onEditItem={handleEditItem} />
        </div>
      </div>

      {/* Product Wizard Modal */}
      {selectedProduct && (() => {
        const editItem = editingItemId ? cart.items.find(it => it.id === editingItemId) : undefined
        const editChildren = editingItemId ? cart.items.filter(it => it.bundle_parent_id === editingItemId) : []
        return (
          <>
            <ProductWizard
              product={selectedProduct}
              isOpen={isWizardOpen}
              onClose={handleWizardClose}
              onAddToCart={handleAddToCart}
              editItem={editItem}
              editChildren={editChildren}
            />
            <BundleWizard
              product={selectedProduct}
              isOpen={isBundleWizardOpen}
              onClose={() => { setIsBundleWizardOpen(false); setSelectedProduct(null); setEditingItemId(null) }}
              onAddToCart={handleAddToCart}
              editItem={editItem}
              editChildren={editChildren}
            />
          </>
        )
      })()}
    </div>
  )
}

export default CashRegistryPage

